// Apolo IDE — ZeroEntropy semantic index client (renderer side).
//
// Reads files via Electron IPC, sends content to Worker for embed + Vectorize.
// Used by: auto-index on session open, Files tab rebuild, write_file incremental.

import { apiUrl, getStoredToken } from './api.js';
import { hasElectronBridge } from './electronBridge.js';

export const INDEX_EXTENSIONS = new Set([
  'js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs', 'mts', 'cts',
  'css', 'scss', 'less', 'html', 'json', 'yaml', 'yml',
  'md', 'mdx', 'py', 'rb', 'go', 'rs', 'java', 'kt',
  'swift', 'c', 'cpp', 'h', 'hpp', 'sql', 'sh', 'bash',
  'zsh', 'fish', 'env', 'toml', 'xml', 'svg',
]);

export const MAX_INDEX_BYTES = 1_000_000;
export const MAX_INDEX_FILES = 2_000;
const INDEX_STALE_MS = 6 * 60 * 60 * 1000; // 6h

function indexStorageKey(root) {
  return `apolo:ze-index:${root}`;
}

export function lastIndexedAt(root) {
  try {
    const raw = localStorage.getItem(indexStorageKey(root));
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function markIndexed(root) {
  try {
    localStorage.setItem(indexStorageKey(root), String(Date.now()));
  } catch {
    /* noop */
  }
}

export function shouldAutoRebuildIndex(root) {
  if (!root?.startsWith('/')) return false;
  const last = lastIndexedAt(root);
  return !last || Date.now() - last > INDEX_STALE_MS;
}

export async function collectIndexableFiles(root) {
  if (!hasElectronBridge()) return [];
  const out = [];
  async function walk(dir = '') {
    const entries = await window.apolo.file.list({ root, dir });
    for (const entry of entries || []) {
      if (entry.isDirectory) {
        await walk(entry.path);
        continue;
      }
      const ext = entry.path.split('.').pop()?.toLowerCase() || '';
      if (!INDEX_EXTENSIONS.has(ext)) continue;
      out.push(entry.path);
      if (out.length >= MAX_INDEX_FILES) return;
    }
  }
  await walk('');
  return out;
}

async function readIndexPayload(root, paths) {
  const payloadFiles = [];
  for (const filePath of paths) {
    try {
      const res = await window.apolo.file.read({ root, filePath });
      const content = res?.content || '';
      if (new TextEncoder().encode(content).length > MAX_INDEX_BYTES) continue;
      payloadFiles.push({
        path: filePath,
        content,
        language: filePath.split('.').pop()?.toLowerCase() || '',
      });
    } catch {
      /* skip unreadable */
    }
  }
  return payloadFiles;
}

/** Full session rebuild via SSE. Returns { ok, chunks?, error? }. */
export async function rebuildSemanticIndex(sessionId, root, { onProgress } = {}) {
  if (!sessionId || !root?.startsWith('/')) {
    return { ok: false, error: 'Sessão ou diretório inválido' };
  }
  if (!hasElectronBridge()) {
    return { ok: false, error: 'Indexação precisa do app Electron' };
  }

  const paths = await collectIndexableFiles(root);
  const payloadFiles = await readIndexPayload(root, paths);
  if (payloadFiles.length === 0) {
    return { ok: false, error: 'Nenhum arquivo indexável encontrado' };
  }

  onProgress?.({ phase: 'upload', done: 0, total: payloadFiles.length });

  const token = getStoredToken();
  const response = await fetch(apiUrl('/api/code-ide/index-rebuild'), {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ session_id: sessionId, files: payloadFiles }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    return { ok: false, error: text || `HTTP ${response.status}` };
  }

  if (!response.body?.getReader) {
    markIndexed(root);
    return { ok: true, chunks: payloadFiles.length };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let chunks = 0;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';
    for (const evt of events) {
      const line = evt.split('\n').find((part) => part.startsWith('data: '));
      if (!line) continue;
      const data = JSON.parse(line.slice(6));
      if (data.type === 'progress') {
        onProgress?.({ phase: 'index', done: data.done, total: data.total });
      } else if (data.type === 'done') {
        chunks = data.chunks || 0;
      }
    }
  }

  markIndexed(root);
  return { ok: true, chunks };
}

/** Index a single file after write_file (best-effort, fire-and-forget). */
export async function indexSingleFile(sessionId, root, filePath, content) {
  if (!sessionId || !root || !filePath || typeof content !== 'string') return { ok: false };
  const ext = filePath.split('.').pop()?.toLowerCase() || '';
  if (!INDEX_EXTENSIONS.has(ext)) return { ok: false, skipped: true };
  if (new TextEncoder().encode(content).length > MAX_INDEX_BYTES) return { ok: false, skipped: true };

  const token = getStoredToken();
  try {
    const res = await fetch(apiUrl('/api/code-ide/index-file'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        session_id: sessionId,
        path: filePath,
        content,
        language: ext,
      }),
    });
    if (!res.ok) return { ok: false };
    markIndexed(root);
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

/** Semantic search via Worker (ZeroEntropy embed + rerank). */
export async function runSemanticSearch(sessionId, query, { topK = 8, useRerank = true } = {}) {
  if (!sessionId || !query?.trim()) {
    return { ok: false, error: 'session_id e query são obrigatórios', matches: [] };
  }
  const token = getStoredToken();
  try {
    const res = await fetch(apiUrl('/api/code-ide/semantic-search'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        session_id: sessionId,
        query: query.trim(),
        top_k: topK,
        use_rerank: useRerank,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: body?.error || body?.details || `HTTP ${res.status}`, matches: [] };
    }
    return { ok: true, matches: body?.matches || body?.data?.matches || [] };
  } catch (e) {
    return { ok: false, error: e?.message || String(e), matches: [] };
  }
}

export function formatSemanticMatches(matches) {
  if (!Array.isArray(matches) || matches.length === 0) return '(sem resultados — indexe o repo na aba Files ou aguarde auto-index)';
  return matches.map((m, i) => {
    const score = typeof m.score === 'number' ? m.score.toFixed(3) : '?';
    const path = m.path || '?';
    const text = String(m.text || '').trim().slice(0, 600);
    return `${i + 1}. ${path} (score ${score})\n${text}`;
  }).join('\n\n');
}
